import React, { useEffect, useMemo, useState, useRef } from "react";
import { Plus, Pencil, Trash2, ChevronDown, ChevronUp, Search, FileDown, RotateCcw, Package, ArrowRightLeft, Upload, X, Info, ChevronLeft, ChevronRight, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtIDR, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Panel } from "../lib/ui";
import OverdueBorrowAlert from "../components/OverdueBorrowAlert";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Switch } from "../components/ui/switch";

const STATUS_STYLE = {
  Aman: "bg-blue-500 text-white", Ready: "bg-amber-400 text-amber-950", Habis: "bg-rose-500 text-white",
};
function StatusPill({ s }) {
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLE[s] || "bg-taupe/30 text-cocoa"}`} data-testid="variant-status">{s}</span>;
}

function KeepBadge({ n }) {
  if (!n || n <= 0) return null;
  return (
    <span
      title={`${n} unit sedang di-Keep (belum keluar)`}
      data-testid="keep-badge"
      className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-400 text-white text-[10px] font-bold align-middle"
    >
      {n}
    </span>
  );
}

function PoCell({ po, keep, awaited }) {
  return (
    <span className="inline-flex items-center gap-1 tabular-nums" data-testid="po-cell">
      <span>{po || 0}</span>
      <span title={`${keep || 0} di-Keep`} className="text-amber-600 font-semibold text-xs" data-testid="po-keep">({keep || 0})</span>
      <span title={`${awaited || 0} sudah di-open PO tapi belum datang`} className="text-red-600 font-semibold text-xs" data-testid="po-awaited">({awaited || 0})</span>
    </span>
  );
}

function ProductSortHead({ children, column, onSort, sortColumn, sortDirection, className = "" }) {
  const active = sortColumn === column;
  return <TableHead className={className}><button type="button" onClick={() => onSort(column)} data-testid={`product-sort-${column}`} className={`inline-flex items-center gap-1 hover:text-cocoa ${active ? "text-cocoa" : ""}`}>{children}{active ? (sortDirection === "asc" ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />) : <ArrowUpDown className="h-3.5 w-3.5" />}</button></TableHead>;
}

export default function Products() {
  const { can } = useAuth();
  const write = can("warehouse_admin");
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});
  const [editId, setEditId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editInfoId, setEditInfoId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortColumn, setSortColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  const load = () => api.get("/products/catalog", { params: { q: q || undefined } }).then((r) => setProducts(r.data.products)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);
  useEffect(() => { setPage(1); }, [q, pageSize]);

  const delProduct = async (p) => {
    if (!window.confirm(`Hapus produk "${p.name}" beserta semua variannya? Produk yang masih punya stok atau pernah terjual/di-keep tidak bisa dihapus.`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success(`Produk "${p.name}" dihapus`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const onSort = (column) => {
    if (sortColumn === column) setSortDirection((direction) => direction === "asc" ? "desc" : "asc");
    else { setSortColumn(column); setSortDirection("asc"); }
    setPage(1);
  };
  const sortedProducts = useMemo(() => [...products].sort((left, right) => {
    const leftValue = left[sortColumn] ?? "";
    const rightValue = right[sortColumn] ?? "";
    const numeric = ["all", "home", "store", "preorder"].includes(sortColumn);
    const comparison = numeric ? Number(leftValue) - Number(rightValue) : String(leftValue).localeCompare(String(rightValue), "id");
    return sortDirection === "asc" ? comparison : -comparison;
  }), [products, sortColumn, sortDirection]);
  const totalPages = Math.max(1, Math.ceil(sortedProducts.length / pageSize));
  const curPage = Math.min(page, totalPages);
  const pageProducts = sortedProducts.slice((curPage - 1) * pageSize, curPage * pageSize);

  const exportCsv = () => {
    const header = "No,Name,Category,All,Home,Store,PreOrder,Variants\n";
    const body = products.map((p, i) => `${i + 1},"${p.name}","${p.category}",${p.all},${p.home},${p.store},${p.preorder},${p.variant_count}`).join("\n");
    const blob = new Blob([header + body], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "products.csv"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="products-page">
      <OverdueBorrowAlert />
      <PageHeader title="Product" subtitle="Katalog produk & varian dengan stok per gudang" />

      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          {write && <Button onClick={() => setCreateOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="product-create-btn"><Plus className="w-4 h-4 mr-1.5" />Create</Button>}
          <Button onClick={exportCsv} className="bg-status-passed hover:bg-status-passed/90 text-white" data-testid="product-export-btn"><FileDown className="w-4 h-4 mr-1.5" />Export</Button>
        </div>
        <div className="relative w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="pl-9 bg-white" data-testid="product-search" />
        </div>
      </div>

      <Panel>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <ProductSortHead className="w-12" column="id" onSort={onSort} sortColumn={sortColumn} sortDirection={sortDirection}>No</ProductSortHead><ProductSortHead column="name" onSort={onSort} sortColumn={sortColumn} sortDirection={sortDirection}>Name</ProductSortHead><ProductSortHead column="category" onSort={onSort} sortColumn={sortColumn} sortDirection={sortDirection}>Category</ProductSortHead>
              <ProductSortHead className="text-center" column="all" onSort={onSort} sortColumn={sortColumn} sortDirection={sortDirection}>All</ProductSortHead><ProductSortHead className="text-center" column="home" onSort={onSort} sortColumn={sortColumn} sortDirection={sortDirection}>Home</ProductSortHead>
              <ProductSortHead className="text-center" column="store" onSort={onSort} sortColumn={sortColumn} sortDirection={sortDirection}>Store</ProductSortHead><ProductSortHead className="text-center" column="preorder" onSort={onSort} sortColumn={sortColumn} sortDirection={sortDirection}><span title="Stok PO: tersedia (di-Keep) (sudah di-open, belum datang)">PO</span></ProductSortHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageProducts.map((p, i) => (
              <React.Fragment key={p.id}>
                <TableRow data-testid={`product-row-${p.id}`}>
                  <TableCell className="text-wmuted">{(curPage - 1) * pageSize + i + 1}</TableCell>
                  <TableCell className="font-semibold text-cocoa">{p.name}</TableCell>
                  <TableCell className="text-sm text-wmuted">{p.category || "-"}</TableCell>
                  <TableCell className="text-center">{p.all}<KeepBadge n={p.all_keep} /></TableCell>
                  <TableCell className="text-center">{p.home}<KeepBadge n={p.home_keep} /></TableCell>
                  <TableCell className="text-center">{p.store}<KeepBadge n={p.store_keep} /></TableCell>
                  <TableCell className="text-center"><PoCell po={p.preorder} keep={p.preorder_keep} awaited={p.preorder_awaited} /></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    {write && <Button size="icon" variant="ghost" onClick={() => setEditInfoId(p.id)} className="h-8 w-8 text-emerald-600" title="Edit informasi produk" data-testid={`product-editinfo-${p.id}`}><Info className="w-4 h-4" /></Button>}
                    {write && <Button size="icon" variant="ghost" onClick={() => setEditId(p.id)} className="h-8 w-8 text-cocoa" title="Edit varian" data-testid={`product-edit-${p.id}`}><Pencil className="w-4 h-4" /></Button>}
                    {write && <Button size="icon" variant="ghost" onClick={() => delProduct(p)} className="h-8 w-8 text-status-reject" title="Hapus produk" data-testid={`product-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>}
                    <Button size="icon" variant="ghost" onClick={() => setExpanded((e) => ({ ...e, [p.id]: !e[p.id] }))} className="h-8 w-8 text-blue-600" data-testid={`product-expand-${p.id}`}>
                      {expanded[p.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                  </TableCell>
                </TableRow>
                {expanded[p.id] && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={8} className="p-0 bg-taupe/5">
                      <Table>
                        <TableHeader>
                          <TableRow className="hover:bg-transparent">
                            <TableHead>Color Size</TableHead><TableHead className="text-right">Selling Price</TableHead>
                            <TableHead className="text-center">All</TableHead><TableHead className="text-center">Home</TableHead>
                            <TableHead className="text-center">Store</TableHead><TableHead className="text-center">Pre Order</TableHead>
                            <TableHead className="text-center">Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {p.variants.map((v) => (
                            <TableRow key={v.variant_id} data-testid={`variant-row-${v.variant_id}`}>
                              <TableCell className="text-sm">{v.color} {v.size}</TableCell>
                              <TableCell className="text-right text-sm">{fmtIDR(v.selling_price)}</TableCell>
                              <TableCell className="text-center">{v.all}<KeepBadge n={v.all_keep} /></TableCell>
                              <TableCell className="text-center">{v.home}<KeepBadge n={v.home_keep} /></TableCell>
                              <TableCell className="text-center">{v.store}<KeepBadge n={v.store_keep} /></TableCell>
                              <TableCell className="text-center"><PoCell po={v.preorder} keep={v.preorder_keep} awaited={v.preorder_awaited} /></TableCell>
                              <TableCell className="text-center"><StatusPill s={v.status} /></TableCell>
                            </TableRow>
                          ))}
                          {!p.variants.length && <TableRow><TableCell colSpan={7} className="text-center text-wmuted py-4 text-sm">Belum ada varian.</TableCell></TableRow>}
                        </TableBody>
                      </Table>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
            {!products.length && <TableRow><TableCell colSpan={8} className="text-center text-wmuted py-10"><Package className="w-6 h-6 mx-auto mb-2 opacity-50" />Tidak ada produk.</TableCell></TableRow>}
          </TableBody>
        </Table>
        {products.length > 0 && (
          <div className="flex items-center justify-between flex-wrap gap-3 mt-3 pt-3 border-t border-taupe/20" data-testid="products-pagination">
            <div className="flex items-center gap-2 text-sm text-wmuted">
              <span>Tampilkan</span>
              <Select value={String(pageSize)} onValueChange={(v) => setPageSize(parseInt(v))}>
                <SelectTrigger className="h-8 w-20 bg-white" data-testid="products-page-size"><SelectValue /></SelectTrigger>
                <SelectContent>{[10, 20, 50].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
              </Select>
              <span>dari {products.length} produk</span>
            </div>
            <div className="flex items-center gap-2">
              <Button size="icon" variant="outline" className="h-8 w-8 border-taupe" disabled={curPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="products-prev-page"><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-sm text-cocoa font-medium">Hal {curPage} / {totalPages}</span>
              <Button size="icon" variant="outline" className="h-8 w-8 border-taupe" disabled={curPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="products-next-page"><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </Panel>

      {editInfoId && <EditProductInfoDialog pid={editInfoId} onClose={() => setEditInfoId(null)} onDone={load} />}
      {editId && <EditProductDialog pid={editId} onClose={() => setEditId(null)} onDone={load} />}
      <CreateProductDialog open={createOpen} setOpen={setCreateOpen} onDone={load} />
    </div>
  );
}

const genImei = () => `VD-${Math.random().toString(36).slice(2, 7).toUpperCase()}${Date.now().toString().slice(-4)}`;

function MultiSelect({ label, options, value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = options.filter((o) => o.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 100);
  const toggle = (o) => onChange(value.includes(o) ? value.filter((x) => x !== o) : [...value, o]);
  return (
    <div ref={ref} className="relative" data-testid={testid}>
      <div className="min-h-[38px] border border-taupe/40 rounded-md bg-white px-2 py-1 flex flex-wrap gap-1 items-center cursor-text" onClick={() => setOpen(true)}>
        {value.map((v) => <span key={v} className="bg-cocoa text-ivory text-xs px-2 py-0.5 rounded-full flex items-center gap-1">{v}<button type="button" onClick={(e) => { e.stopPropagation(); toggle(v); }}><X className="w-3 h-3" /></button></span>)}
        <input value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} placeholder={value.length ? "" : `Cari ${label}...`} className="flex-1 min-w-[90px] outline-none text-sm py-1 bg-transparent" data-testid={`${testid}-input`} />
      </div>
      {open && (
        <div className="absolute z-30 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-taupe/30 rounded-md shadow-lg">
          {filtered.map((o) => <button key={o} type="button" onClick={() => { toggle(o); setQ(""); }} className={`block w-full text-left px-3 py-1.5 text-sm hover:bg-cocoa/5 ${value.includes(o) ? "font-semibold text-cocoa" : ""}`} data-testid={`${testid}-opt-${o}`}>{value.includes(o) ? "✓ " : ""}{o}</button>)}
          {!filtered.length && <div className="px-3 py-2 text-sm text-wmuted">Tidak ada.</div>}
        </div>
      )}
    </div>
  );
}

function CreateProductDialog({ open, setOpen, onDone }) {
  const empty = { name: "", imei: "", status: "ACTIVE", category: "", selling_price: "", purchase_price: "", launch_date: "", description: "", main_photo: "", sizes: [], colors: [] };
  const [form, setForm] = useState(empty);
  const [cats, setCats] = useState([]);
  const [colorOpts, setColorOpts] = useState([]);
  const [sizeOpts, setSizeOpts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...empty, imei: genImei() });
    api.get("/products/categories").then((r) => setCats(r.data || [])).catch(() => {});
    api.get("/products/colors").then((r) => setColorOpts(r.data || [])).catch(() => {});
    api.get("/products/sizes").then((r) => setSizeOpts(r.data || [])).catch(() => {});
    // eslint-disable-next-line
  }, [open]);

  const toggle = (key, val) => setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, main_photo: r.data.path }));
      toast.success("Foto terunggah");
    } catch (err) { toast.error("Upload foto gagal"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama produk wajib diisi");
    setSaving(true);
    try {
      await api.post("/products", {
        name: form.name, imei: form.imei || null, status: form.status, active: form.status === "ACTIVE",
        category: form.category, main_photo: form.main_photo || null,
        selling_price: parseInt(form.selling_price) || null, purchase_price: parseInt(form.purchase_price) || null,
        launch_date: form.launch_date || null, description: form.description,
        sizes: form.sizes, colors: form.colors, variants: [],
      });
      toast.success("Produk dibuat"); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const Chip = ({ active, onClick, children }) => (
    <button type="button" onClick={onClick} className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${active ? "bg-cocoa text-ivory border-cocoa" : "bg-white text-cocoa border-taupe/40 hover:border-cocoa"}`}>{children}</button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-product-dialog">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Create Product</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="shrink-0">
              <Label>Foto Produk</Label>
              <label className="mt-1 w-28 h-28 rounded-lg border-2 border-dashed border-taupe/40 flex items-center justify-center cursor-pointer overflow-hidden bg-white hover:border-cocoa" data-testid="product-photo-drop">
                {form.main_photo ? <img src={fileUrl(form.main_photo)} alt="produk" className="w-full h-full object-cover" /> : <span className="text-xs text-wmuted text-center px-2">{uploading ? "Mengunggah..." : <><Upload className="w-5 h-5 mx-auto mb-1" />Unggah</>}</span>}
                <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="product-photo-input" />
              </label>
              {form.main_photo && <button onClick={() => setForm({ ...form, main_photo: "" })} className="text-xs text-status-reject mt-1 flex items-center gap-1" data-testid="product-photo-remove"><X className="w-3 h-3" />Hapus</button>}
            </div>
            <div className="flex-1 space-y-3">
              <div><Label>Nama Produk *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="create-product-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>IMEI / Kode</Label><Input value={form.imei} onChange={(e) => setForm({ ...form, imei: e.target.value })} data-testid="create-product-imei" /></div>
                <div><Label>Product Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="create-product-status"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="ACTIVE">Active</SelectItem><SelectItem value="INACTIVE">Inactive</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Category</Label>
                <Input list="cat-list" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Pilih / ketik kategori" data-testid="create-product-category" />
                <datalist id="cat-list">{cats.map((c) => <option key={c} value={c} />)}</datalist>
              </div>
              <div><Label>Tanggal Launching <span className="text-xs text-wmuted font-normal">(opsional — untuk product life cycle)</span></Label>
                <Input type="date" value={form.launch_date} onChange={(e) => setForm({ ...form, launch_date: e.target.value })} data-testid="create-product-launch" /></div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Harga Jual</Label><Input type="number" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} placeholder="0" data-testid="create-product-selling" /></div>
            <div><Label>Harga Beli</Label><Input type="number" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} placeholder="0" data-testid="create-product-purchase" /></div>
          </div>

          <div>
            <Label>Warna <span className="text-xs text-wmuted font-normal">(multi-select, dari data yang ada)</span></Label>
            <MultiSelect label="warna" options={colorOpts} value={form.colors} onChange={(v) => setForm((f) => ({ ...f, colors: v }))} testid="create-product-colors" />
          </div>
          <div>
            <Label>Ukuran <span className="text-xs text-wmuted font-normal">(multi-select, dari data yang ada)</span></Label>
            <MultiSelect label="ukuran" options={sizeOpts} value={form.sizes} onChange={(v) => setForm((f) => ({ ...f, sizes: v }))} testid="create-product-sizes" />
          </div>
          {(form.colors.length > 0 || form.sizes.length > 0) && (
            <p className="text-xs text-wmuted">Akan dibuat <b className="text-cocoa">{Math.max(form.colors.length, 1) * Math.max(form.sizes.length, 1)}</b> varian dari kombinasi warna × ukuran.</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} disabled={saving || uploading} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="save-product-btn">{saving ? "Menyimpan..." : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const STATUSES = ["ACTIVE", "INACTIVE"];

function EditProductInfoDialog({ pid, onClose, onDone }) {
  const empty = { name: "", imei: "", status: "ACTIVE", category: "", selling_price: "", purchase_price: "", launch_date: "", description: "", main_photo: "", keep_as_po: false };
  const [form, setForm] = useState(empty);
  const [cats, setCats] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products/categories").then((r) => setCats(r.data || [])).catch(() => {});
    api.get(`/products/${pid}`).then((r) => {
      const p = r.data || {};
      setForm({
        name: p.name || "", imei: p.imei || "", status: p.status || (p.active === false ? "INACTIVE" : "ACTIVE"),
        category: p.category || "", selling_price: p.selling_price != null ? String(p.selling_price) : "",
        purchase_price: p.purchase_price != null ? String(p.purchase_price) : "",
        launch_date: p.launch_date ? String(p.launch_date).slice(0, 10) : "",
        description: p.description || "", main_photo: p.main_photo || "", keep_as_po: !!p.keep_as_po,
      });
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [pid]);

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, main_photo: r.data.path }));
      toast.success("Foto terunggah");
    } catch (err) { toast.error("Upload foto gagal"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nama produk wajib diisi");
    setSaving(true);
    try {
      await api.put(`/products/${pid}`, {
        name: form.name, imei: form.imei || null, status: form.status, active: form.status === "ACTIVE",
        category: form.category, main_photo: form.main_photo || null,
        selling_price: parseInt(form.selling_price) || null, purchase_price: parseInt(form.purchase_price) || null,
        launch_date: form.launch_date || null, description: form.description, keep_as_po: form.keep_as_po,
      });
      toast.success("Informasi produk diperbarui"); onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="editinfo-product-dialog">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Edit Information Product</DialogTitle></DialogHeader>
        {loading ? <div className="py-10 text-center text-wmuted text-sm">Memuat...</div> : (
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="shrink-0">
                <Label>Foto Produk</Label>
                <label className="mt-1 w-28 h-28 rounded-lg border-2 border-dashed border-taupe/40 flex items-center justify-center cursor-pointer overflow-hidden bg-white hover:border-cocoa" data-testid="editinfo-photo-drop">
                  {form.main_photo ? <img src={fileUrl(form.main_photo)} alt="produk" className="w-full h-full object-cover" /> : <span className="text-xs text-wmuted text-center px-2">{uploading ? "Mengunggah..." : <><Upload className="w-5 h-5 mx-auto mb-1" />Unggah</>}</span>}
                  <input type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="editinfo-photo-input" />
                </label>
                {form.main_photo && <button onClick={() => setForm({ ...form, main_photo: "" })} className="text-xs text-status-reject mt-1 flex items-center gap-1" data-testid="editinfo-photo-remove"><X className="w-3 h-3" />Hapus</button>}
              </div>
              <div className="flex-1 space-y-3">
                <div><Label>Nama Produk *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="editinfo-product-name" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>IMEI / Kode</Label><Input value={form.imei} onChange={(e) => setForm({ ...form, imei: e.target.value })} data-testid="editinfo-product-imei" /></div>
                  <div><Label>Product Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger data-testid="editinfo-product-status"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s === "ACTIVE" ? "Active" : "Inactive"}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Category</Label>
                  <Input list="editinfo-cat-list" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Pilih / ketik kategori" data-testid="editinfo-product-category" />
                  <datalist id="editinfo-cat-list">{cats.map((c) => <option key={c} value={c} />)}</datalist>
                </div>
                <div><Label>Tanggal Launching <span className="text-xs text-wmuted font-normal">(opsional — untuk product life cycle)</span></Label>
                  <Input type="date" value={form.launch_date} onChange={(e) => setForm({ ...form, launch_date: e.target.value })} data-testid="editinfo-product-launch" /></div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Harga Jual</Label><Input type="number" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} placeholder="0" data-testid="editinfo-product-selling" /></div>
              <div><Label>Harga Beli</Label><Input type="number" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} placeholder="0" data-testid="editinfo-product-purchase" /></div>
            </div>
            <div><Label>Deskripsi</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Keterangan produk (opsional)" data-testid="editinfo-product-desc" /></div>
            <div className="flex items-center justify-between rounded-lg border border-taupe/30 bg-cocoa/5 px-4 py-3">
              <div>
                <Label className="text-cocoa font-semibold">Selalu simpan sebagai Stok PO</Label>
                <p className="text-xs text-wmuted mt-0.5">Kedatangan &amp; sisa produk ini tetap jadi Stok PO siap jual — tidak bisa dipindah ke stok reguler.</p>
              </div>
              <Switch checked={form.keep_as_po} onCheckedChange={(v) => setForm({ ...form, keep_as_po: v })} data-testid="editinfo-keep-as-po" />
            </div>
            <p className="text-xs text-wmuted">Varian (warna/ukuran) dikelola melalui tombol Edit varian, tidak diubah di sini.</p>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} disabled={saving || uploading || loading} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="editinfo-save-btn">{saving ? "Menyimpan..." : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EditProductDialog({ pid, onClose, onDone }) {
  const [prod, setProd] = useState(null);
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [form, setForm] = useState({ color: "", size: "", status: "ACTIVE", purchase_price: "", selling_price: "", editing: null });
  const [vq, setVq] = useState("");
  const [transferVar, setTransferVar] = useState(null);

  const load = () => api.get("/products/catalog", { params: { q: undefined } }).then((r) => setProd(r.data.products.find((x) => x.id === pid)));
  const loadOne = async () => {
    const r = await api.get("/products/catalog");
    setProd(r.data.products.find((x) => x.id === pid));
  };
  useEffect(() => { loadOne(); api.get("/products/colors").then((r) => setColors(r.data)).catch(() => {}); api.get("/products/sizes").then((r) => setSizes(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, [pid]);

  const margin = (() => {
    const pp = parseInt(form.purchase_price) || 0, sp = parseInt(form.selling_price) || 0;
    if (!pp || !sp) return 0;
    return Math.round(((sp - pp) / pp) * 100);
  })();

  const resetForm = () => setForm({ color: "", size: "", status: "ACTIVE", purchase_price: "", selling_price: "", editing: null });

  const saveVariant = async () => {
    const payload = { color: form.color, size: form.size, status: form.status, purchase_price: parseInt(form.purchase_price) || 0, selling_price: parseInt(form.selling_price) || 0 };
    if (!payload.color && !payload.size) return toast.error("Pilih warna/ukuran");
    try {
      if (form.editing) await api.put(`/products/variants/${form.editing}`, payload);
      else await api.post(`/products/${pid}/variants`, payload);
      toast.success("Varian tersimpan"); resetForm(); loadOne(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const editVariant = (v) => setForm({ color: v.color, size: v.size, status: "ACTIVE", purchase_price: String(v.purchase_price || ""), selling_price: String(v.selling_price || ""), editing: v.variant_id });
  const delVariant = async (v) => {
    if (!window.confirm(`Hapus varian ${v.color} ${v.size}?`)) return;
    try { await api.delete(`/products/variants/${v.variant_id}`); toast.success("Varian dihapus"); loadOne(); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const variants = (prod?.variants || []).filter((v) => !vq || `${v.color} ${v.size}`.toLowerCase().includes(vq.toLowerCase()));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Edit {prod?.name || ""}</DialogTitle></DialogHeader>

        <div className="border border-taupe/20 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label>Color</Label>
              <Select value={form.color || undefined} onValueChange={(v) => setForm({ ...form, color: v })}>
                <SelectTrigger className="bg-white" data-testid="edit-color-select"><SelectValue placeholder="Select Color" /></SelectTrigger>
                <SelectContent className="max-h-72">{colors.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Size</Label>
              <Select value={form.size || undefined} onValueChange={(v) => setForm({ ...form, size: v })}>
                <SelectTrigger className="bg-white" data-testid="edit-size-select"><SelectValue placeholder="Select Size" /></SelectTrigger>
                <SelectContent className="max-h-72">{sizes.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Product Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-white" data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Purchase Price</Label><Input type="number" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} placeholder="Purchase Price" className="bg-white" data-testid="edit-purchase-price" /></div>
            <div><Label>Selling Price</Label><Input type="number" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} placeholder="Selling Price" className="bg-white" data-testid="edit-selling-price" /></div>
            <div><Label>Margin</Label><Input value={`${margin}%`} disabled className="bg-taupe/10" data-testid="edit-margin" /></div>
          </div>
          <div className="flex justify-end mt-3">
            <Button onClick={saveVariant} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-variant-btn">{form.editing ? "Update" : "Save"}</Button>
          </div>
        </div>

        <div className="border border-taupe/20 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="font-semibold text-cocoa">Daftar Jenis Product</span>
            <div className="relative w-56"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-wmuted" /><Input value={vq} onChange={(e) => setVq(e.target.value)} placeholder="Search by color or size" className="pl-9 bg-white h-9" data-testid="variant-search" /></div>
          </div>
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent"><TableHead className="w-10">No</TableHead><TableHead>Color Size</TableHead><TableHead className="text-right">Selling Price</TableHead><TableHead className="text-right">Purchase Price</TableHead><TableHead className="text-center">All</TableHead><TableHead className="text-center">Home</TableHead><TableHead className="text-center">Store</TableHead><TableHead className="text-center">Pre Order</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
            <TableBody>
              {variants.map((v, i) => (
                <TableRow key={v.variant_id} data-testid={`edit-variant-row-${v.variant_id}`}>
                  <TableCell className="text-wmuted">{i + 1}</TableCell>
                  <TableCell className="text-sm">{v.color} {v.size}</TableCell>
                  <TableCell className="text-right text-sm">{fmtIDR(v.selling_price)}</TableCell>
                  <TableCell className="text-right text-sm">{fmtIDR(v.purchase_price)}</TableCell>
                  <TableCell className="text-center">{v.all}</TableCell>
                  <TableCell className="text-center">{v.home}</TableCell>
                  <TableCell className="text-center">{v.store}</TableCell>
                  <TableCell className="text-center"><PoCell po={v.preorder} keep={v.preorder_keep} awaited={v.preorder_awaited} /></TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" onClick={() => editVariant(v)} className="h-8 w-8 text-blue-600" title="Edit info varian" data-testid={`edit-variant-btn-${v.variant_id}`}><RotateCcw className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => setTransferVar(v)} className="h-8 w-8 text-amber-600" title="Transfer stok" data-testid={`transfer-variant-btn-${v.variant_id}`}><ArrowRightLeft className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => delVariant(v)} className="h-8 w-8" title="Hapus varian" data-testid={`del-variant-btn-${v.variant_id}`}><Trash2 className="w-4 h-4 text-status-reject" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {!variants.length && <TableRow><TableCell colSpan={9} className="text-center text-wmuted py-6 text-sm">Belum ada varian.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-taupe text-cocoa">Cancel</Button>
          <Button onClick={onClose} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="edit-done-btn">Save</Button>
        </DialogFooter>
        {transferVar && <TransferStockDialog v={transferVar} productName={prod?.name || ""} onClose={() => setTransferVar(null)} onDone={() => { loadOne(); onDone(); }} />}
      </DialogContent>
    </Dialog>
  );
}

function TransferStockDialog({ v, productName, onClose, onDone }) {
  const [whs, setWhs] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [qty, setQty] = useState("1");
  useEffect(() => {
    api.get(`/products/variants/${v.variant_id}/stock`).then((r) => setWhs(r.data.warehouses)).catch(() => {});
  }, [v.variant_id]);
  const fromQty = whs.find((w) => w.warehouse_id === from)?.qty ?? 0;
  const save = async () => {
    if (!from || !to) return toast.error("Pilih gudang asal & tujuan");
    if (from === to) return toast.error("Gudang asal & tujuan sama");
    const n = parseInt(qty) || 0;
    if (n <= 0) return toast.error("Qty harus > 0");
    if (n > fromQty) return toast.error(`Stok asal hanya ${fromQty}`);
    try {
      await api.post("/transfers/quick", { from_warehouse: from, to_warehouse: to, notes: "Transfer dari Product",
        items: [{ product_id: v.product_id, variant_id: v.variant_id, product_name: productName, color: v.color, size: v.size, qc_status: "passed", qty: n }] });
      toast.success("Transfer selesai"); onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading text-cocoa">Transfer Stok — {v.color} {v.size}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Dari Gudang</Label>
            <Select value={from || undefined} onValueChange={setFrom}>
              <SelectTrigger className="bg-white" data-testid="transfervar-from"><SelectValue placeholder="Pilih gudang asal" /></SelectTrigger>
              <SelectContent>{whs.map((w) => <SelectItem key={w.warehouse_id} value={w.warehouse_id}>{w.warehouse_name} (stok: {w.qty})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Ke Gudang</Label>
            <Select value={to || undefined} onValueChange={setTo}>
              <SelectTrigger className="bg-white" data-testid="transfervar-to"><SelectValue placeholder="Pilih gudang tujuan" /></SelectTrigger>
              <SelectContent>{whs.map((w) => <SelectItem key={w.warehouse_id} value={w.warehouse_id}>{w.warehouse_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Qty {from && <span className="text-xs text-wmuted">(maks {fromQty})</span>}</Label><Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} className="bg-white" data-testid="transfervar-qty" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-taupe text-cocoa">Batal</Button>
          <Button onClick={save} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="transfervar-save">Transfer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
